import { Box, Divider, IconButton, Popover } from '@mui/material';
import { personRef } from '@timelog/pm-shared';
import type { PersonRef, RoleRef } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { personSubtitle } from '../personRole';
import type { RoleOption, RolePricing } from '../useRoleOptions';

import { useEmployeeLookup } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import { formatAmount, useDebouncedValue } from '@/lib';
import { roleColor } from '@/lib/roleColor';

/**
 * The timeline's staffing picker (v5, Area 2): from the bar's add affordance
 * it runs two steps — Choose Role, then optionally Or Pick A Person; from an
 * existing role chip it opens directly in assign mode (Assign A Person, with
 * Edit resources and Remove allocation). The person step is type-to-search
 * against the lookup, not a scroll of the whole register. MUI's Popover
 * handles flip/clamp against the viewport.
 */

const SectionLabel = ({ children }: { children: string }) => (
  <Box
    sx={{
      px: 1.5,
      py: 0.5,
      fontSize: 10,
      fontWeight: 500,
      letterSpacing: MICRO_LABEL_TRACKING,
      textTransform: 'uppercase',
      color: 'var(--fg-3)',
    }}
  >
    {children}
  </Box>
);

const OptionRow = ({
  onClick,
  automationId,
  destructive = false,
  children,
}: {
  onClick: () => void;
  automationId?: string;
  destructive?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    component="button"
    type="button"
    data-automation-id={automationId}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      px: 1.5,
      py: 0.75,
      border: 'none',
      bgcolor: 'transparent',
      fontSize: 11,
      fontFamily: 'var(--font-family)',
      color: destructive ? 'var(--color-coral-80)' : 'var(--fg-1)',
      textAlign: 'left',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      '&:hover': { bgcolor: destructive ? 'var(--color-coral-10)' : 'var(--bg-hover)' },
    }}
  >
    {children}
  </Box>
);

const RoleSwatch = ({ tlpId, size = 8 }: { tlpId: string; size?: number }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      // A filled circle, matching the avatars the same colour fills elsewhere.
      borderRadius: '50%',
      bgcolor: roleColor(tlpId),
      flex: 'none',
    }}
  />
);

const PersonList = ({
  role,
  lookupsEnabled,
  pricing,
  onPick,
}: {
  role: RoleRef;
  lookupsEnabled: boolean;
  pricing: RolePricing | undefined;
  onPick: (person: PersonRef) => void;
}) => {
  /*
    Type-to-search, debounced, straight into the lookup — the list is a page of
    the whole employee register, so scrolling it was only ever workable while
    the register was a fixture. The overlay's person field has had this from
    the start (an Autocomplete); this list was the one that had not.
  */
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);
  const lookup = useEmployeeLookup(debounced, lookupsEnabled);
  const { t } = useTranslation('pmPlanningPanel');
  // People whose default role IS the chosen role float to the top.
  const items = [...(lookup.data?.items ?? [])].sort(
    (a, b) =>
      Number(b.defaultRoleServiceId === role.tlpId) - Number(a.defaultRoleServiceId === role.tlpId),
  );

  return (
    <>
      <Box sx={{ px: 1.5, pb: 0.75 }}>
        <Box
          component="input"
          autoFocus
          placeholder={t('rolePicker.SearchPeople')}
          data-automation-id="timeline-person-search"
          value={query}
          onChange={(event) => setQuery((event.target as HTMLInputElement).value)}
          // The picker's own Escape closes the popover; typing must not reach
          // the plan editor's global key handling behind it.
          onKeyDown={(event) => event.stopPropagation()}
          sx={{
            width: '100%',
            height: 'var(--input-height-small)',
            px: 1,
            border: 'var(--border-width-thin) solid var(--border-divider)',
            borderRadius: 'var(--border-radius-small)',
            bgcolor: 'var(--bg-panel)',
            fontFamily: 'var(--font-family)',
            fontSize: 11,
            color: 'var(--fg-1)',
            outline: 'none',
            '&:focus': { borderColor: 'var(--color-blue-80)' },
          }}
        />
      </Box>
      <Box sx={{ maxHeight: 210, overflowY: 'auto' }}>
        {items.map((item) => (
          <OptionRow
            key={item.tlpId}
            automationId="timeline-person-option"
            onClick={() => {
              onPick(personRef(item.tlpId, item.name, new Date().toISOString(), item.costRates));
            }}
          >
            <Box
              component="span"
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: roleColor(role.tlpId),
                color: 'var(--color-black-00)',
                fontSize: 10,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              {item.name.charAt(0)}
            </Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {item.name}
            </Box>
            {item.defaultRoleName || (pricing === 'cost-to-cost' && item.costRates) ? (
              <Box component="span" sx={{ fontSize: 10, color: 'var(--fg-3)' }}>
                {personSubtitle(item, pricing, t)}
              </Box>
            ) : null}
          </OptionRow>
        ))}
        {!lookupsEnabled || items.length === 0 ? (
          <Box sx={{ px: 1.5, py: 1, fontSize: 11, fontStyle: 'italic', color: 'var(--fg-3)' }}>
            {lookupsEnabled
              ? query === ''
                ? t('rolePicker.NoPeopleFound')
                : t('rolePicker.NoPeopleMatch', { query })
              : t('rolePicker.PeopleLookupDisabled')}
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export type RolePickerPopoverProps = {
  anchorEl: HTMLElement | null;
  /** add = Choose Role → Or Pick A Person; assign = resolve an existing role chip. */
  mode: 'add' | 'assign';
  roleOptions: RoleOption[];
  /**
   * How the listed roles price (D15). Under `cost-to-cost` the rows show the
   * cost price instead of a sell rate, because that is what revenue derives
   * from (CALC-24); absent = show sell rates where there are any.
   */
  pricing?: RolePricing;
  /** Why there is nothing to choose, when there is nothing to choose. */
  notice?: string | null;
  lookupsEnabled: boolean;
  /** The chip's role in assign mode; ignored in add mode. */
  assignRole?: RoleRef;
  onAdd: (role: RoleRef, person: PersonRef | undefined) => void;
  onAssign: (person: PersonRef | undefined) => void;
  onRemove: () => void;
  /**
   * Assign mode only: hands the allocation over to the list's full Resources
   * editor, the one surface that can change its hours. Without it a role-only
   * chip could be assigned or removed and nothing else.
   */
  onEditResources: () => void;
  onClose: () => void;
};

export const RolePickerPopover = ({
  anchorEl,
  mode,
  roleOptions,
  pricing,
  notice = null,
  lookupsEnabled,
  assignRole,
  onAdd,
  onAssign,
  onRemove,
  onEditResources,
  onClose,
}: RolePickerPopoverProps) => {
  const [chosenRole, setChosenRole] = useState<RoleRef | null>(null);
  const { t } = useTranslation('pmPlanningPanel');
  const role = mode === 'assign' ? (assignRole ?? null) : chosenRole;

  const header = (label: string, withBack: boolean) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pb: 0.5 }}>
      {withBack ? (
        <IconButton
          size="tiny"
          title={t('rolePicker.BackToRoles')}
          aria-label={t('rolePicker.BackToRoles')}
          data-automation-id="timeline-picker-back"
          onClick={() => setChosenRole(null)}
        >
          <MaterialSymbol name="chevron_left" size={15} />
        </IconButton>
      ) : null}
      {role ? <RoleSwatch tlpId={role.tlpId} size={7} /> : null}
      <Box
        component="span"
        sx={{
          flex: 1,
          fontSize: role ? 11 : 10,
          fontWeight: 500,
          ...(role
            ? {}
            : {
                letterSpacing: MICRO_LABEL_TRACKING,
                textTransform: 'uppercase',
                color: 'var(--fg-3)',
              }),
        }}
      >
        {label}
      </Box>
      <IconButton
        size="tiny"
        title={t('shared.Close')}
        aria-label={t('shared.Close')}
        onClick={onClose}
      >
        <MaterialSymbol name="close" size={15} />
      </IconButton>
    </Box>
  );

  return (
    <Popover
      open={anchorEl !== null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      data-automation-id="timeline-role-picker"
      slotProps={{
        paper: {
          sx: {
            // Fits the longest row rather than a fixed 250: a person row reads
            // "Name  Role · 700 kr/h cost" on ONE line, and never wraps.
            width: 'max-content',
            minWidth: 250,
            maxWidth: 480,
            py: 0.75,
            border: 'var(--border-width-thin) solid var(--border-subtle)',
            borderRadius: 'var(--border-radius-medium)',
            boxShadow: 'var(--shadow-small)',
          },
        },
      }}
    >
      {role === null ? (
        <>
          {header(t('rolePicker.ChooseRole'), false)}
          {notice ? (
            <Box
              data-automation-id="timeline-role-notice"
              sx={{ px: 1.5, py: 1, fontSize: 11, color: 'var(--fg-3)' }}
            >
              {notice}
            </Box>
          ) : null}
          {roleOptions.map((option) => (
            <OptionRow
              key={`${option.source}-${option.roleRef.tlpId}`}
              automationId="timeline-role-option"
              onClick={() => setChosenRole(option.roleRef)}
            >
              <RoleSwatch tlpId={option.roleRef.tlpId} />
              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {option.roleRef.snapshot.name}
              </Box>
              {pricing === 'cost-to-cost' ? (
                // Revenue derives from cost (CALC-24): the traceable figure is the cost price.
                <Box
                  component="span"
                  sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 10, color: 'var(--fg-3)' }}
                >
                  {option.costRate !== null
                    ? t('shared.CostPerHour', { amount: formatAmount(option.costRate) })
                    : '—'}
                </Box>
              ) : option.rate !== null ? (
                <Box
                  component="span"
                  sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 10, color: 'var(--fg-3)' }}
                >
                  {formatAmount(option.rate)}
                </Box>
              ) : null}
              <MaterialSymbol name="chevron_right" size={15} color="var(--fg-3)" />
            </OptionRow>
          ))}
          <Box sx={{ px: 1.5, pt: 0.75, fontSize: 10, color: 'var(--fg-3)', lineHeight: 1.45 }}>
            {t('rolePicker.AddRoleHint')}
          </Box>
        </>
      ) : (
        <>
          {header(role.snapshot.name, mode === 'add')}
          <OptionRow
            automationId="timeline-any-role"
            onClick={() => {
              if (mode === 'add') onAdd(role, undefined);
              else onAssign(undefined);
            }}
          >
            <Box
              component="span"
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: `var(--border-width-thin) dashed ${roleColor(role.tlpId)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              <MaterialSymbol name="person" size={13} color="var(--fg-3)" />
            </Box>
            {/* "Anyone", not "Any {role}" — the role is already the header. */}
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {t('shared.Anyone')}
            </Box>
            <Box component="span" sx={{ fontSize: 10, color: 'var(--fg-3)' }}>
              {t('rolePicker.NoPersonYet')}
            </Box>
          </OptionRow>
          <Divider sx={{ my: 0.5 }} />
          <SectionLabel>
            {mode === 'add' ? t('rolePicker.OrPickAPerson') : t('rolePicker.AssignAPerson')}
          </SectionLabel>
          <PersonList
            role={role}
            lookupsEnabled={lookupsEnabled}
            pricing={pricing}
            onPick={(person) => {
              if (mode === 'add') onAdd(role, person);
              else onAssign(person);
            }}
          />
          {mode === 'assign' ? (
            <>
              <Divider sx={{ my: 0.5 }} />
              <OptionRow automationId="timeline-edit-resources" onClick={onEditResources}>
                <MaterialSymbol name="group_add" size={15} color="var(--color-purple-80)" />
                {t('rolePicker.EditResources')}
              </OptionRow>
              <OptionRow automationId="timeline-remove-allocation" destructive onClick={onRemove}>
                <MaterialSymbol name="close" size={15} color="var(--color-coral-80)" />
                {t('rolePicker.RemoveAllocation')}
              </OptionRow>
            </>
          ) : null}
        </>
      )}
    </Popover>
  );
};
